import { backendPropertiesCrud } from '@services/housien';

type PropertyRecord = Record<string, unknown>;

/**
 * Indexes a property record into the lookup map under both its `id` and
 * `property_id` keys (whichever are present), so lookups by either identifier
 * resolve to the same record.
 *
 * @param map - Mutable lookup map keyed by property identifier.
 * @param p - Property record to index.
 */
function indexProperty(map: Record<string, PropertyRecord>, p: PropertyRecord): void {
  if (p.id) map[p.id as string] = p;
  if (p.property_id) map[p.property_id as string] = p;
}

/**
 * Builds a property lookup map from agency properties already available in
 * context (array or record form), avoiding an API call. Returns null when no
 * context properties are present, signaling the caller to fall back to the API.
 *
 * @param agencyProperties - Context properties as an array or keyed record.
 * @returns The lookup map, or null when context has no properties.
 */
function indexFromContext(agencyProperties: unknown): Record<string, PropertyRecord> | null {
  const contextProps = Array.isArray(agencyProperties)
    ? agencyProperties
    : Object.values(agencyProperties ?? {});

  if (contextProps.length === 0) return null;

  const map: Record<string, PropertyRecord> = {};
  (contextProps as PropertyRecord[]).forEach((p) => indexProperty(map, p));
  return map;
}

/**
 * Fetches up to 200 agency properties from the backend and indexes them into a
 * lookup map. Swallows fetch errors and returns whatever was indexed (possibly
 * empty), so visit rows degrade to showing raw IDs rather than failing.
 *
 * @param agencyId - Agency whose properties are fetched.
 * @returns A lookup map keyed by property identifier.
 */
async function indexFromApi(agencyId: string): Promise<Record<string, PropertyRecord>> {
  const map: Record<string, PropertyRecord> = {};
  try {
    const response = await backendPropertiesCrud(agencyId).search({
      pagination: { page: '1', limit: '200' },
    });
    const data = response.data;
    if (!data) return map;
    const items = Array.isArray(data) ? data : Object.values(data);
    (items as PropertyRecord[]).forEach((p) => indexProperty(map, p));
  } catch {
    // fallback — IDs show as-is
  }
  return map;
}

/**
 * Builds a property lookup map for visit display, preferring in-context
 * properties and falling back to a backend fetch when context is empty.
 *
 * @param agencyId - Agency used for the API fallback.
 * @param agencyProperties - Properties already loaded in context, if any.
 * @returns A lookup map keyed by property identifier.
 */
export async function buildPropertiesMap(
  agencyId: string,
  agencyProperties: unknown,
): Promise<Record<string, PropertyRecord>> {
  return indexFromContext(agencyProperties) ?? indexFromApi(agencyId);
}
